import './style.scss'
import { Button, message, Modal, Skeleton, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import VirtualList from 'rc-virtual-list'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import * as HomeApi from '@/api/home'
import { Play } from '@/assets/svg'
import { Back } from '@/assets/svg/back'
import { IconRandom } from '@/assets/svg/icon-random'
import CheckLogin from '@/components/CheckLogin'
import PayPointModal from '@/components/PayPointModal'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import {
  checkSensitive,
  downlad,
  eventTracking,
  Render,
  showErrorwordsTip,
  showPlanModal,
  translateHtml
} from '@/libs/util'
import { DeleteOutlined, DownloadOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import AIGeneratorModal from '../components/AIGenerator'
import HighlightTextarea from '../components/HighlightTextArea'

const AnsmePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { loginStatus } = UserStore
  const params: any = useParams()
  const id = +(params?.id as any)
  const [config, setConfig] = useState<any>(undefined)
  const [characters, setCharacters] = useState<any[]>([])
  const [subtitleEnabled, setSubtitleEnabled] = useState(true)
  const [activeCharacter, setActiveCharacter] = useState(0)
  const [paypointModalOpen, setPayPointModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [videoList, setVideoList] = useState<any>(undefined as any)
  const [pagination, setPagination] = useState({ current: 0, pageSize: 10, total: 0 })
  const [videoLoading, setVideoLoading] = useState(false)
  const [videoId, setVideoId] = useState(-1)
  const [height, setHeight] = useState(0)
  const listRef = useRef<any>(null)
  const loadingRef = useRef(false)
  const [preview, setPreview] = useState<any>()
  const [itemHeight, setItemHeight] = useState(0)
  const rightRef = useRef<any>()
  const intervalRef = useRef<NodeJS.Timeout>()
  const inputRef = useRef<any>()
  const [audioUrls, setAudioUrls] = useState<any>()
  const [inputText, setInputText] = useState('')
  const [AIGeneratorModalOpen, setAIGeneratorModalOpen] = useState(false)

  useEffect(() => {
    eventTracking('AnsmePage', { id })
    inputRef.current.initText(
      '你天天说要减肥，你咋不说你嘴巴馋，它那么爱吃，那我问你。我让你少吃一块炸鸡，你会不会瘦？会不会？我让你多动一下是害你吗？[回答我] 我办健身卡花你钱了？嗯，你回答我！[Look in my eyes] [Tell me why] 我让你去跑步，我错了吗？奶茶是神仙水吗？说增肌就要多吃的人都来了，那我再问你啊，你一顿吃三个汉堡。你第二天也敢上称吗？[回答我] 你吃完夜宵躺床上啊，能瘦吗？[哎能能] 你看夏天别人穿吊带吓不吓你呀！'
    )
    UserStore.initSensitiveWords()
    setTimeout(() => {
      getConfig()
      setVideoList([])
      getVideoList()
      updateVirtualConfig()
    }, 300)
    initItemHeight()

    intervalRef.current = setInterval(updateVideoStatuses, 10000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (loginStatus) {
      getVideoList()
    }
  }, [loginStatus])

  useEffect(() => {
    if (characters?.length) {
      setAudioUrls(characters[activeCharacter]?.audio_clip_urls || [])
    }
  }, [activeCharacter, characters])

  const updateVideoStatuses = async () => {
    try {
      const res = await HomeApi.getAnswermeVideos({
        id,
        size: pagination.pageSize,
        page: pagination.current,
        di: UserStore.device_id
      })

      if (res?.list?.length) {
        setVideoList((prevList: any[]) => {
          return prevList.map((item) => {
            const updatedItem = res.list.find((v: any) => v.id === item.id)
            if (updatedItem && item.status === 1) {
              return updatedItem
            }
            return item
          })
        })
      }
    } catch (error) {
      console.error('Failed to update video statuses:', error)
    }
  }

  const initItemHeight = () => {
    const dom = rightRef.current
    const w = dom?.offsetWidth
    if (w) {
      setItemHeight((w - 80) * (9 / 16) + 56)
    }
  }

  const getConfig = async () => {
    const res = await HomeApi.getAnswermeConfig({
      id
    })
    setConfig(res)
    setCharacters(res?.roles || [])
  }

  const updateVirtualConfig = () => {
    const h = window.innerHeight - 160
    setHeight(h)
  }

  const getVideoList = async (page = 0, pageSize = 10) => {
    if (loadingRef.current) return
    loadingRef.current = true
    try {
      setVideoLoading(true)
      const res = await HomeApi.getAnswermeVideos({
        id,
        size: pageSize,
        page
      })

      setVideoList(page === 0 ? res?.list || [] : [...(videoList || []), ...(res?.list || [])])
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: res?.total || 0
      }))
    } catch (error: any) {
      setVideoList([])
    } finally {
      setVideoLoading(false)
      loadingRef.current = false
    }
  }

  const handleLoadMore = useCallback(() => {
    if (videoLoading || videoList?.length >= pagination.total) return
    const nextPage = pagination.current + 1
    getVideoList(nextPage, pagination.pageSize)
  }, [pagination, videoLoading])

  const handleDeleteVideo = async (item: any) => {
    Modal.confirm({
      title: t('answerme.delete_title'),
      content: t('answerme.delete_content'),
      onOk: async () => {
        await HomeApi.deleteAnswermeVideos(item.id)
        setVideoList(videoList?.filter((p: any) => p.id !== item.id))
        message.success(t('answerme.delete_success'))
      }
    })
  }

  const playVideo = (item: any) => {
    setVideoId(item.id)
  }

  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      handleLoadMore()
    }
  }

  const handleGenerate = async () => {
    if (!inputText.trim()) {
      return message.warning('请输入文案')
    }
    submit()
  }

  const submit = async () => {
    const { width, height } = characters[activeCharacter]
    const fontSize = 80
    const titleHeight = fontSize * 1.5 * (Math.max(width, height) / 1920)
    const transText = translateHtml(inputText)?.replace(
      /<break class="highlight-tag" text="([^"]+)"><\/break>/g,
      '[$1]'
    )

    const checkText = transText.replace(/\[(回答我|Tell me why|Look in my eyes|哎能能)\]/g, '')

    if (!checkText.trim()) {
      return message.warning('文案不能全是标签')
    }

    if (transText.length > 10000) {
      return message.warning('文案过长')
    }

    const errorWords = checkSensitive([{ words: transText }])

    if (errorWords?.length) {
      return showErrorwordsTip(errorWords)
    }

    const params: any = {
      id,
      role_index: activeCharacter,
      text: transText
    }

    if (subtitleEnabled) {
      params.subtitle = {
        font_name: '抖音美好体',
        font_size: fontSize,
        primary_color: '#ffffff',
        outline_color: '#000000',
        width: Math.floor(width * 0.8),
        height: Math.floor(titleHeight),
        pos_x: Math.floor(width * 0.1),
        pos_y: Math.floor(height * 0.85)
      }
    }

    try {
      setLoading(true)
      const res = await HomeApi.submitAnswermeVideos(params)
      if (res.sensitive_words) {
        showErrorwordsTip(res.sensitive_words)
      } else {
        message.success(t('answerme.submit_success'))
        listRef.current?.scrollTo(0)
      }

      setPagination({ current: 0, pageSize: 10, total: 0 })
      getVideoList(0, pagination.pageSize)
    } catch (error: any) {
      if (error.code === 1002) {
        setPayPointModalOpen(true)
      }
      if (error.code === 1003 || error.code === 1004 || error.code === 1005) {
        showPlanModal()
      }
    } finally {
      setLoading(false)
    }
  }

  const onTextChange = (text: string) => {
    setInputText(text)
  }

  return (
    <div className="page-common page-ansme">
      <div className="common-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <Back />
        </button>
        <span>{t('answerme.page_title')}</span>
      </div>
      <div className="common-wrapper">
        <main className="page-content">
          <div className="main-container">
            <div className="controls-panel">
              <div className="panel-content">
                <div>
                  <div className="card-header">
                    <h2>{t('answerme.select_role')}</h2>
                    <a
                      href="https://lingverse.feishu.cn/share/base/form/shrcnDZmesjEh8domQ2kENCnJm5"
                      target="_blank"
                      className="customize-host-link gradient"
                      rel="noreferrer"
                    >
                      {t('answerme.customize_role')}
                    </a>
                  </div>
                  <div className="card character-selection">
                    {characters?.length ? (
                      <>
                        {characters?.map((character, index) => (
                          <div
                            key={character.id}
                            className={`character-card ${activeCharacter === index ? 'active' : ''}`}
                            onClick={() => setActiveCharacter(index)}
                          >
                            <div className="checkmark">✓</div>
                            <div className="img-box">
                              <img src={character.cover_url} alt={character.name} />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <Skeleton active />
                    )}
                  </div>
                </div>

                <div>
                  <div className="card-header">
                    <h2>{t('picture.script')}</h2>
                    <Button
                      className="gray-btn"
                      type="default"
                      icon={<IconRandom />}
                      onClick={() => setAIGeneratorModalOpen(true)}
                    >
                      {t('answerme.ai_generator')}
                    </Button>
                  </div>
                  <div className="card">
                    <HighlightTextarea onTextChange={onTextChange} audioUrls={audioUrls} ref={inputRef} />
                  </div>
                </div>

                <div>
                  <div className="card-header">
                    <h2>{t('answerme.subtitle')}</h2>
                    <Switch checked={!!subtitleEnabled} onChange={(v) => setSubtitleEnabled(v)} />
                  </div>
                </div>
              </div>
              <div className="action-footer">
                <p className="cost-breakdown">
                  <Tooltip
                    color="#fff"
                    title={
                      <a
                        target="_blank"
                        href="https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0"
                        rel="noreferrer"
                      >
                        {t('answerme.points_explanation')}
                      </a>
                    }
                  >
                    {t('answerme.points_title')}&nbsp;
                    <span className="tooltip-container">
                      <i className="info-icon">i</i>
                    </span>
                  </Tooltip>
                </p>
                <CheckLogin>
                  <Button loading={loading} className="footer-btn" type="primary" onClick={handleGenerate}>
                    ✨ {t('answerme.submit_button')}
                  </Button>
                </CheckLogin>
              </div>
            </div>
            <div className="main-right" ref={rightRef}>
              {!videoList || !config || !itemHeight ? (
                <div className="skeleton">
                  <Skeleton active />
                </div>
              ) : (
                <>
                  {videoList?.length === 0 ? (
                    <div className="right-title">
                      <span>{t('answerme.example_effect')}</span>
                    </div>
                  ) : (
                    <div className="right-title">
                      <span>{t('answerme.latest_works')}</span>
                      <span
                        className="link gradient"
                        onClick={() => {
                          setPreview({
                            url: config?.demo_video_urls?.[0],
                            title: t('answerme.example_effect')
                          })
                        }}
                      >
                        {t('answerme.example_effect')}
                      </span>
                    </div>
                  )}

                  <div className="video-list-container">
                    {videoList?.length === 0 && config?.demo_video_urls ? (
                      <div className="video-box">
                        <video className="video" src={config?.demo_video_urls?.[0]} controls autoPlay muted />
                      </div>
                    ) : (
                      <VirtualList
                        data={videoList || []}
                        height={height}
                        itemHeight={itemHeight}
                        itemKey="id"
                        onScroll={onScroll}
                        ref={listRef}
                      >
                        {(item: any) => (
                          <div className="video-item" key={item.id} data-id={item.id}>
                            <Render>
                              {function List() {
                                const [percent, setPercent] = useState(-1)
                                const xhrRef = useRef<any>()
                                const downladVideo = (d: any) => {
                                  if (xhrRef.current) {
                                    xhrRef.current.abort()
                                    setPercent(0)
                                  }
                                  const url = d.url.split('?')[0]
                                  const extension = url.substring(url.lastIndexOf('.')).toLowerCase()
                                  const xhr: XMLHttpRequest = downlad(
                                    d.url,
                                    `${d.title || t('answerme.untitled')}${extension}`
                                  )
                                  xhr.onprogress = (e) => {
                                    const p = Math.floor((e.loaded / e.total) * 100)
                                    setPercent(p)
                                    if (p === 100) {
                                      setTimeout(() => {
                                        setPercent(-1)
                                      }, 300)
                                    }
                                  }
                                  xhrRef.current = xhr
                                }

                                return (
                                  <>
                                    <div className="video-preview">
                                      {item.id === videoId ? (
                                        <video src={item.url} poster={item.preview_url} controls autoPlay />
                                      ) : (
                                        <img src={item.preview_url} alt="Preview" />
                                      )}

                                      {item.status === 1 && (
                                        <div className="mask">
                                          <div className="pending">
                                            <LoadingOutlined />
                                            <div>{t('answerme.generating')}</div>
                                          </div>
                                        </div>
                                      )}

                                      {item.status === 2 && item.id !== videoId && (
                                        <div className="play" onClick={() => playVideo(item)}>
                                          <Play />
                                        </div>
                                      )}

                                      {item.status === 3 && (
                                        <div className="mask">
                                          <Tooltip placement="bottom" title={item.error_message || ''}>
                                            <span className="status error">
                                              {t('answerme.generation_failed')}{' '}
                                              {item.error_message && <InfoCircleOutlined />}
                                            </span>
                                          </Tooltip>
                                        </div>
                                      )}

                                      {percent > -1 && (
                                        <div className="mask">
                                          <div className="pending">
                                            <LoadingOutlined />
                                            <div>{t('answerme.downloading', { percent })}</div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="video-info">
                                      <span className="create-time">
                                        {dayjs.unix(item.create_time).format('YYYY-MM-DD HH:mm:ss')}
                                      </span>
                                      {item.status !== 1 && (
                                        <Tooltip placement="top" title={t('answerme.delete')}>
                                          <Button
                                            className="download"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDeleteVideo(item)}
                                          ></Button>
                                        </Tooltip>
                                      )}

                                      {item.status === 2 && (
                                        <Tooltip placement="top" title={t('answerme.download')}>
                                          <Button
                                            className="download"
                                            icon={<DownloadOutlined />}
                                            onClick={() => downladVideo(item)}
                                          ></Button>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </>
                                )
                              }}
                            </Render>
                          </div>
                        )}
                      </VirtualList>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      <AIGeneratorModal
        open={AIGeneratorModalOpen}
        onCancel={() => setAIGeneratorModalOpen(false)}
        onSuccess={(text: string) => inputRef.current?.initText(text)}
      />

      <PayPointModal open={paypointModalOpen} onCancel={() => setPayPointModalOpen(false)} />

      <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />
    </div>
  )
}

export default AnsmePage
